import React from "react";
import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";

import "./CDataTable.scss";
import { CSpinnerText } from "../CSpinnerText";
import { useDarkMode } from "../hooks/useDarkMode";

export interface CDataTableColumn<T = any> {
  columnKey: string;
  columnLabel: string;
  columnStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  cellRender?: (item: T) => React.ReactNode;
  sortable?: boolean;
}

// Note: Generic components can't use React.FC, so we use explicit function typing
interface Props<T = any> {
  columns: CDataTableColumn<T>[];
  items: T[];
  striped?: boolean;
  small?: boolean;
  hover?: boolean;
  responsive?: boolean;
  className?: string;
  getRowProps?: (item: T) => { className?: string; id?: string; [key: string]: any };
  getCellProps?: (item: T, column: CDataTableColumn<T>) => { id?: string; [key: string]: any };
  /** Stable React key per row. Defaults to the item's `id`, falling back to its position. Provide this when items get re-sorted or filtered and the records have no `id`. */
  getRowKey?: (item: T, index: number) => React.Key;
  loading?: boolean;
  /** Number of column groups to display side by side. Items distribute column-first (top-to-bottom, then left-to-right). */
  nbColumns?: number;
  /** Explicit number of rows per column group. When set with nbColumns > items needed, extra groups become invisible spacers that absorb remaining width. */
  nbRows?: number;
}

// Re-export Props type as CDataTableProps for external use
export type CDataTableProps<T = any> = Props<T>;

export const CDataTable = <T,>(props: Props<T>): React.ReactElement => {
  const {
    columns,
    items,
    striped,
    small,
    hover,
    responsive,
    className,
    getRowProps,
    getCellProps,
    getRowKey,
    loading = false,
    nbColumns = 1,
    nbRows,
  } = props;

  const { isDark } = useDarkMode();
  const isMultiColumn = nbColumns > 1;

  const getHeaderStyle = (column: CDataTableColumn<T>) => {
    return { ...column.columnStyle, ...column.headerStyle };
  };

  const getCellStyle = (column: CDataTableColumn<T>) => {
    return { ...column.columnStyle, ...column.cellStyle };
  };

  // Index keys make React reuse a row node for whatever record lands in that slot after a
  // re-sort, so anything the node owns (cell state, focus, poppers) follows the position
  // instead of the record.
  const resolveRowKey = (item: T, index: number): React.Key => {
    if (getRowKey) return getRowKey(item, index);
    const id = (item as { id?: unknown } | null | undefined)?.id;
    return typeof id === "string" || typeof id === "number" ? id : index;
  };

  // Automatically apply dark mode class
  const tableClassName = [className, isDark ? "table-dark" : ""].filter(Boolean).join(" ");

  // Multi-column: split items into column groups (column-first distribution)
  const rowCount = isMultiColumn ? (nbRows ?? Math.ceil(items.length / nbColumns)) : items.length;
  const columnGroups: T[][] = [];
  if (isMultiColumn) {
    for (let g = 0; g < nbColumns; g++) {
      columnGroups.push(items.slice(g * rowCount, (g + 1) * rowCount));
    }
  }

  // Detect which groups are spacers (no items at all)
  const isSpacerGroup = (g: number) =>
    isMultiColumn && items.length > 0 && columnGroups[g].length === 0;

  // Calculate total colSpan accounting for separators between non-spacer groups
  // and colSpan for spacer groups
  const computeTotalColSpan = () => {
    if (!isMultiColumn) return columns.length;
    let span = 0;
    for (let g = 0; g < nbColumns; g++) {
      if (g > 0 && !isSpacerGroup(g)) span += 1; // separator
      if (isSpacerGroup(g)) {
        span += 1; // single spacer cell
      } else {
        span += columns.length;
      }
    }
    return span;
  };
  const totalColSpan = computeTotalColSpan();

  const renderLoadingRow = () => {
    return (
      <CTableRow>
        <CTableDataCell colSpan={totalColSpan} className="text-center py-4">
          <CSpinnerText text="Loading..." gap="2" />
        </CTableDataCell>
      </CTableRow>
    );
  };

  const renderItemsRows = (items: T[]) => {
    return items.map((item, index) => {
      const rowProps = getRowProps ? getRowProps(item) : {};
      return (
        <CTableRow key={resolveRowKey(item, index)} {...rowProps}>
          {columns.map((column) => {
            const cellProps = getCellProps ? getCellProps(item, column) : {};
            return (
              <CTableDataCell key={column.columnKey} style={getCellStyle(column)} {...cellProps}>
                {column.cellRender
                  ? column.cellRender(item)
                  : String(item[column.columnKey as keyof T] ?? "")}
              </CTableDataCell>
            );
          })}
        </CTableRow>
      );
    });
  };

  const renderMultiColumnHeader = () => {
    const cells: React.ReactNode[] = [];
    for (let g = 0; g < nbColumns; g++) {
      if (isSpacerGroup(g)) {
        // Spacer group: single empty cell that absorbs remaining width
        cells.push(<CTableHeaderCell key={`spacer-${g}`} className="CDataTable-spacer" />);
      } else {
        if (g > 0) {
          cells.push(<CTableHeaderCell key={`sep-${g}`} className="CDataTable-separator" />);
        }
        for (const column of columns) {
          cells.push(
            <CTableHeaderCell key={`${g}-${column.columnKey}`} style={getHeaderStyle(column)}>
              {column.columnLabel}
            </CTableHeaderCell>,
          );
        }
      }
    }
    return <CTableRow>{cells}</CTableRow>;
  };

  const renderMultiColumnRows = () => {
    const rows: React.ReactNode[] = [];
    for (let r = 0; r < rowCount; r++) {
      const cells: React.ReactNode[] = [];
      for (let g = 0; g < nbColumns; g++) {
        if (isSpacerGroup(g)) {
          cells.push(<CTableDataCell key={`spacer-${g}`} className="CDataTable-spacer" />);
        } else {
          if (g > 0) {
            cells.push(<CTableDataCell key={`sep-${g}`} className="CDataTable-separator" />);
          }
          const item = columnGroups[g][r];
          if (r < columnGroups[g].length) {
            for (const column of columns) {
              const cellProps = getCellProps ? getCellProps(item, column) : {};
              cells.push(
                <CTableDataCell
                  key={`${g}-${column.columnKey}`}
                  style={getCellStyle(column)}
                  {...cellProps}
                >
                  {column.cellRender
                    ? column.cellRender(item)
                    : String(item[column.columnKey as keyof T] ?? "")}
                </CTableDataCell>,
              );
            }
          } else {
            for (const column of columns) {
              cells.push(<CTableDataCell key={`${g}-${column.columnKey}`} />);
            }
          }
        }
      }
      // A visual row here spans one record per group, so its identity is the combination of them.
      // Serialized rather than delimiter-joined, so a key containing the delimiter can't collide.
      const rowKey = JSON.stringify(
        columnGroups.map((group, g) =>
          r < group.length ? resolveRowKey(group[r], g * rowCount + r) : null,
        ),
      );
      rows.push(<CTableRow key={rowKey}>{cells}</CTableRow>);
    }
    return rows;
  };

  return (
    <CTable
      striped={striped}
      small={small}
      hover={hover}
      responsive={responsive}
      className={tableClassName}
    >
      <CTableHead>
        {isMultiColumn ? (
          renderMultiColumnHeader()
        ) : (
          <CTableRow>
            {columns.map((column) => (
              <CTableHeaderCell key={column.columnKey} style={getHeaderStyle(column)}>
                {column.columnLabel}
              </CTableHeaderCell>
            ))}
          </CTableRow>
        )}
      </CTableHead>

      <CTableBody>
        {loading
          ? renderLoadingRow()
          : isMultiColumn
            ? renderMultiColumnRows()
            : renderItemsRows(items)}
      </CTableBody>
    </CTable>
  );
};
